import {
  type AuftraggeberBuergschaftBearbeitenWorkflowSchema,
  type AuftraggeberBuergschaftHinzufuegenWorkflowSchema,
  type AuftraggeberBuergschaftVerwaltenWorkflowSchema,
  type AuftraggeberInanspruchnahmeWorkflowSchema,
  type AvallinieBearbeitenWorkflowSchema,
  type AvallinieHinzufuegenWorkflowSchema,
  type AvallinieVerwaltenWorkflowSchema,
  type BuergschaftBeauftragenWorkflowSchema,
  type TeilenthaftungEintragenWorkflowSchema,
  type TextvorlageHinzufuegenWorkflowSchema,
  type VertragAnlegenWorkflowSchema,
  type VertragBearbeitenWorkflowSchema,
  type VertragDetailSiteSchema,
  type VertragZuordnenWorkflowSchema,
} from '@/lang/types/types.ts';
import type { StammdatenVerwaltenAuftraggeberWorkflowSchema } from '@/lang/types/auftraggeber/unternehmen.ts';
import type { ProjektFormSchema } from '@/lang/types/auftraggeber/projekt.ts';
import type { TabellenKonfigurationSchema } from '@/lang/types/auftraggeber/buergschaft.ts';
import type { ZweitpruefenWorkflowSchema } from '@/lang/types/auftraggeber/auftrag.ts';
import type { EinstellungenWorkflowSchema } from '@/lang/types/auftraggeber/einstellungen.ts';

export const stammdatenGesellschaftDatenAuftraggeberFormular: StammdatenVerwaltenAuftraggeberWorkflowSchema =
  {
    banner: {
      eingaben: {
        header: 'Bitte überprüfen Sie alle Eingaben vor dem Speichern',
        text: 'Beachten Sie, dass die Unternehmensdaten nach dem Speichern nur von Trustlog-Mitarbeiter:innen bearbeitet werden können.',
      },
      beachten: {
        header: 'Bitte beachten Sie',
        text:
          'Beachten Sie, dass die Unternehmensdaten nach dem Speichern nur von Trustlog-Mitarbeiter:innen bearbeitet werden können.\n' +
          'Sollten Sie eine eingeschränkte Sichtberechtigung auf die Bürgschaften verwenden, ordnen Sie bitte Ihre Mitarbeiter:innen bei Bedarf dem Unternehmen zu.',
      },
    },
    header: {
      titel: {
        hinzufuegen: 'Unternehmen hinzufügen',
        daten: 'Details zum Unternehmen',
      },
    },
    sections: {
      header: {
        titel: 'Angaben zum Unternehmen',
        hinweisPruefen: 'Bitte prüfen Sie die Angaben des Unternehmens.',
      },
      firmierung: {
        label: 'Firmierung',
        nameHandelsregister: {
          label: 'Name laut Handelsregister',
          validationName: 'name',
        },
      },
      anschrift: {
        label: 'Anschrift',
      },
      weitereAngaben: {
        label: 'Weitere Angaben',
        gesellschaftsform: {
          label: 'Gesellschaftsform',
          validationName: 'gesellschaftsform',
        },
        handelsregisternummer: {
          label: 'Handelsregisternummer',
          validationName: 'handelsregisternummer',
        },
        handelsregister: {
          label: 'Handelsregister',
          validationName: 'handelsregister',
        },
      },
    },
  };

export const textvorlageHinzufuegenWorkflow: TextvorlageHinzufuegenWorkflowSchema = {
  contentHeader: 'Textvorlage hinzufügen',
  banner: {
    info: {
      headline: 'Offene Aufgabe',
      text: 'Zu dieser Bürgschaftsart ist noch eine offene Aufgabe zu erledigen. Wollen Sie diese zuerst bearbeiten?',
    },
    error: {
      headline: 'Text bereits vorhanden',
      text: 'Zu den gewählten Eigenschaften existiert bereits ein Text. Sie können ein anderes Gültigkeitsdatum wählen oder den Vorgang abbrechen.',
    },
  },
  steps: {
    textart: {
      title: 'Textart auswählen',
      beschreibung:
        'Bitte wählen Sie, ob Sie einen für Ihr Unternehmen erstellten Auftraggebertext, einen öffentlichen Text oder die Standardtexte der teilnehmenden Bürgen nutzen möchten.',
      error: 'Bitte wählen Sie eine Textart aus',
      gruppen: {
        buergschaftsart: 'Bürgschaftsart',
        textart: 'Textart',
      },
      buergschaftsart: 'Bürgschaftsart',
      radioOptionen: {
        eigene: {
          label: 'Eigene Vorlage hinzufügen',
          subheading:
            'Laden Sie eine eigene Vorlage hoch. Der Inhalt wird anschließend von den teilnehmenden Bürgen geprüft.',
        },
        oeffentlich: {
          label: 'Öffentlichen Text verwenden',
          subheading:
            'Verwenden Sie einen öffentlichen Mustertext, um diesen umgehend Ihren Auftragnehmern zur Verfügung zu stellen.',
        },
        buergen: {
          label: 'Bürgentext verwenden',
          subheading:
            'Wählen Sie die Mustertexte der Bürgen, um diese umgehend Ihren Auftragnehmern zur Verfügung zu stellen.',
        },
      },
    },
    konfigurieren: {
      titleBuergen: 'Bürgentext konfigurieren',
      titleEigen: 'Eigene Vorlage konfigurieren',
      titleOeffentlich: 'Öffentlichen Text konfigurieren',
      beschreibung:
        'Wählen Sie die Eigenschaften für Ihre Bürgschaften aus, die Ihren Auftragnehmern bei der Beauftragung von Bürgschaften vorgeblendet werden sollen.',
      beschreibungEigen:
        'Bitte laden Sie Ihre eigene Textvorlage als Datei hoch. Diese wird einmalig von den Bürgen geprüft. Der Text wird nach Freigabe Ihren Auftragnehmern bei der Beauftragung einer Bürgschaft automatisch zur Verfügung gestellt.',
      gruppen: {
        baurecht: 'Baurecht',
        befristung: 'Befristung',
        geltungsbeginn: 'Geltungsbeginn',
        textart: 'Textart',
        textvorlage: 'Textvorlage',
        interneTextbezeichnung: 'Interne Textbezeichnung',
        gueltigFuerBuergen: 'Bürgen',
      },
      interneTextbezeichnung: {
        field: 'Bezeichnung',
        error: 'Bitte geben Sie mindestens 5 Zeichen ein',
      },
      textart: {
        field: 'Art des öffentlichen Textes',
      },
      geltungsbeginn: {
        field: 'Geltungsbeginn',
        inline:
          'Das Datum bezieht sich auf das Vertragsdatum Ihrer Bau- oder Werkverträge. Bei der erstmaligen Textanlage sollte das Datum möglichst weit in der Vergangenheit liegen, um auch für weiter zurückliegende Vorhaben digitale Bürgschaften zu erhalten.',
      },
      gueltigFuerBuergen: {
        inline: 'Die Textvorlage wird den unterstehenden Bürgen zur Prüfung vorgelegt.',
      },
      statusDate: 'Stand {statusDate}',
    },
    pruefen: {
      title: 'Eingaben prüfen',
      beschreibungEigen:
        'Bitte überprüfen Sie Ihre angegebenen Daten, bevor Sie den Text absenden und geben Sie optional einen Ansprechpartner für mögliche Rückfragen der Bürgen an.',
      beschreibungBuergen:
        'Bitte überprüfen Sie Ihre angegebenen Daten, bevor Sie den Bürgentext aktivieren.',
      beschreibungOeffentlich:
        'Bitte überprüfen Sie Ihre angegebenen Daten, bevor Sie den öffentlichen Text aktivieren.',
      gruppen: {
        texteigenschaften: 'Texteigenschaften',
        textvorlage: 'Textvorlage',
        ansprechpartner: 'Ansprechpartner',
      },
      header: {
        befristung: 'Befristung',
        buergschaftsart: 'Bürgschaftsart',
        baurecht: 'Baurecht',
        bezeichnung: 'Bezeichnung',
        geltungsbeginn: 'Geltungsbeginn',
        buergen: 'Bürgen',
        status: 'Status',
        textart: 'Art des öff. Textes',
      },
      ansprechpartner: {
        vorname: 'Vorname',
        nachname: 'Nachname',
        telefonnummer: 'Telefonnummer',
        email: 'E-Mail-Adresse',
        meineKontaktdatenEintragen: 'Meine Kontaktdaten eintragen',
      },
      preview: {
        headline: 'Vorschau eigener Textvorlage',
        loadingText: 'Das Dokument wird geladen...',
      },
      button: {
        dokumentAnsehen: 'Dokument ansehen',
        absenden: 'Text absenden',
        aktivieren: 'Text aktivieren',
      },
    },
  },
};

export const burgschaftBearbeitenWorkflow: AuftraggeberBuergschaftBearbeitenWorkflowSchema = {
  contentHeaderEingegangen: 'Eingegangene Bürgschaft bearbeiten',
  contentHeaderAusgestellt: 'Ausgestellte Bürgschaft bearbeiten',
  hinweisBannerBearbeitet: 'Die Bürgschaft wurde bearbeitet.',
};

export const buergschaftHinzufuegenWorkflow: AuftraggeberBuergschaftHinzufuegenWorkflowSchema = {
  contentHeaderDefault: 'Bürgschaft hinzufügen',
  steps: {
    auswahl: {
      title: 'Auftraggeber- oder Auftragnehmerrolle wählen',
      beschreibung:
        'Wählen Sie aus, ob Sie die Bürgschaft als kreditorische oder als debitorische Bürgschaft zu Ihrem Bestand hinzufügen möchten.',
      radioOptionen: {
        eingegangen: {
          label: 'Eingegangene Bürgschaft hinzufügen',
          subheading:
            'Sie fügen eine kreditorische Bürgschaft als Auftraggeber zur Liste der eingegangenen Bürgschaften hinzu.',
        },
        ausgestellt: {
          label: 'Ausgestellte Bürgschaft hinzufügen',
          subheading:
            'Sie fügen eine debitorische Bürgschaft als Auftragnehmer zur Liste der ausgestellten Bürgschaften hinzu.',
        },
      },
    },
  },
};

export const buergschaftVerwaltenWorkflow: AuftraggeberBuergschaftVerwaltenWorkflowSchema = {
  steps: {
    beteiligte: {
      title: 'Vertragsdaten eingeben',
      beschreibung:
        'Bitte geben Sie die Daten des Bürgen, der die Bürgschaft ausgestellt hat, sowie die der beiden Vertragspartner ein.',

      neuerBuerge: 'Die Anlage von fehlenden Bürgen können Sie hier beantragen.',
      gruppen: {
        buerge: 'Bürge',
        vertragspartner: 'Vertragspartner',
        kreditnehmer: 'Auftragnehmer',
        beguenstigter: 'Auftraggeber',
      },
      fehler: {
        vertragspartner: 'Bitte wählen Sie die Vertragspartner',
        kreditnehmer: 'Bitte wählen Sie einen Auftragnehmer',
        beguenstigter: 'Bitte wählen Sie einen Auftraggeber',
      },
      suche: {
        hinzufuegenLabel: {
          kreditnehmer: 'Daten manuell eingeben',
          beguenstigter: 'Daten manuell eingeben',
        },
        suchLabel: {
          kreditnehmer: 'Auftragnehmer',
          beguenstigter: 'Auftraggeber',
        },
      },
      eingabe: {
        typ: {
          firma: 'Firma',
          oeffentlich: 'Öffentlich',
          privatperson: 'Privatperson',
        },
        suchButton: 'zurück zur Suche',
        name: 'Vorname und Nachname',
        nameLautHandelsregister: 'Name laut Handelsregister',
        gruppe: {
          kreditnehmer: 'Auftragnehmer',
          beguenstigter: 'Auftraggeber',
        },
        manuell: 'Daten manuell eingeben',
      },
      einladung: {
        title: 'Auftragnehmer einladen, digitale Bürgschaften zu beantragen',
        beschreibung:
          'Da der ausgewählte Bürge an Trustlog teilnimmt, hätte diese Bürgschaft digital sein können. Sparen Sie in Zukunft die manuelle Eingabe und nutzen Sie die Vorteile digitaler Bürgschaften, indem Sie den Auftragnehmer davon überzeugen, Bürgschaften für Sie digital ausstellen zu lassen.',
        checkbox:
          'Ja, senden Sie mir bitte eine E-Mail-Vorlage, mit der ich meine Auftragnehmer über die Vorteile digitaler Bürgschaften informieren kann und in der erklärt wird, wie sie diese in Zukunft bei ihren Bürgen beantragen können. (optional)',
      },
    },
    erfassung: {
      title: 'Bürgschaftsdaten erfassen',
      beschreibung:
        'Bitte geben Sie die Bürgschaftsdaten so ein, wie sie auf dem Dokument angegeben sind.',
      gruppen: {
        status: 'Status der Bürgschaft',
        buergschaft: 'Bürgschaft',
        vertrag: 'Bau- oder Werkvertrag',
        laufzeit: 'Laufzeit',
        artDerArbeit: 'Art der Arbeit',
        ortDerArbeit: 'Ort der Arbeit',
        dokument: 'Bürgschaftsdokument',
      },
      felder: {
        buergschaftsnummer: 'Bürgschaftsnummer',
        buergschaftsart: 'Bürgschaftsart',
        buergschaftsbetrag: 'Bürgschaftsbetrag',
        ausstellungsdatum: 'Ausstellungsdatum',
        vertragsdatum: 'Vertragsdatum',
        vertragsnummer: 'Vertragsnummer',
        laufzeit: {
          befristung: {
            unbefristet: 'Unbefristet',
            befristet: 'Befristet',
            ablaufDatum: 'Vorauss. Ablaufdatum (optional)',
            befristungsDatum: 'Befristungsdatum',
          },
        },
        artDerArbeitPlaceholder:
          'Hier können Sie einen Hinweis über die Art der Arbeit hinterlassen …',
        ortDerArbeit: {
          freitext: 'Ort beschreiben',
          adresse: 'Adresse eingeben',
          ortFreitextPlaceholder: 'Bitte beschreiben Sie hier den Ort der Arbeit möglichst genau…',
        },
        dokument: {
          maxErreicht: 'Sie haben bereits die maximale Anzahl von Dokumenten ausgewählt',
          uploading: 'Wird hochgeladen...',
          fehler:
            'Es ist ein Fehler beim Hochladen Ihres Dokuments aufgetreten.\nBitte entfernen Sie diese Datei und versuchen Sie eine in einem anderen Format hochzuladen.',
        },
      },
      hinweise: {
        intro: 'Bitte geben Sie die Daten so ein, wie sie auf der Bürgschaft beschrieben sind.',
        statusFehler: 'Bitte wählen Sie eine Option aus.',
      },
    },
    pruefen: {
      title: 'Eingaben prüfen',
      beschreibung:
        'Bitte überprüfen Sie die von Ihnen angegebenen Daten. Nachdem Sie die Bürgschaft hinzufügt haben, wird diese in Ihrer Bürgschaftsliste angezeigt.',
      aktenvermerk: 'Aktenvermerk',
      felder: {
        internerVermerk: 'Interner Vermerk',
      },
      rows: {
        inanspruchnahme: {
          title: 'Inanspruchnahme',
          subtitles: {
            hoehe: 'Höhe der Inanspruchnahme',
            aktenzeichen: 'Aktenzeichen',
            grund: 'Grund der Inanspruchnahme',
            ansprechpartner: 'Ansprechpartner',
            anschrift: 'Anschrift',
            unterlagen: 'Unterlagen',
          },
        },
        nutzererklaerung: {
          title: 'Nutzererklärung',
          fehler: 'Bitte stimmen Sie der Nutzererklärung zu, um den Prozess fortzuführen',
        },
      },
    },
  },
};

export const avallinieVerwaltenWorkflow: AvallinieVerwaltenWorkflowSchema = {
  banner: {
    erfolg: {
      textHinzufuegen: 'Die Avallinie wurde hinzugefügt.',
      textBearbeiten: 'Die Änderungen wurden gespeichert.',
    },
    warnung: {
      text: 'Die dargestellte Auslastung basiert auf den in Trustlog erfassten Bürgschaften und Anträgen, gegebenenfalls ergänzt durch von Ihnen hinterlegten Stichtagswerte. Für die Qualität der Daten sind Sie selbst verantwortlich.',
    },
  },
  steps: {
    kreditvertrag: {
      title: 'Kreditvertrag auswählen',
      beschreibung:
        'Bitte wählen Sie den oder die Kreditverträge aus, die Sie mit der Avallinie verknüpfen möchten. Ein Kreditvertrag kann jeweils nur mit einer Avallinie verknüpft werden.',
      input: {
        headline: 'Vertragsverknüpfung',
        buerge: {
          label: 'Bürge',
          beantragen: 'Die Anlage von fehlenden Bürgen können Sie hier beantragen.',
        },
        kreditvertrag: {
          label: 'Kreditvertrag',
        },
      },
      loeschen: {
        button: 'Avallinie löschen',
        modal: {
          headline: 'Löschen bestätigen',
          description:
            'Sind Sie sicher, dass Sie diese Avallinie löschen möchten? Verlinkte Kreditverträge, Bürgschaften und Aufträge bleiben bestehen und werden nicht gelöscht. <br>' +
            'Diese Aktion kann nicht rückgängig gemacht werden.',
          button: 'Avallinie löschen',
        },
      },
    },
    rahmen: {
      title: 'Rahmen konfigurieren',
      aktionen: {
        hinzufuegen: 'Avallinie hinzufügen',
      },
      beschreibung:
        'Bitte geben Sie die mit dem Bürgen vereinbarte Linienhöhe ein und legen Sie bei Bedarf den Höchstbetrag je Bürgschaft fest. Optional können Sie noch Sublimits einrichten.',
      rahmenvertragsmodell: {
        headline: 'Rahmenvertragsmodell',
        fehlermeldung: 'Bitte wählen Sie eine Option aus.',
        gesamtrahmen: {
          label: 'Überjähriger Gesamtrahmen',
        },
        jahresrahmen: {
          label: 'Jahresrahmen für Verbandsmitglieder',
        },
      },
      gesamtrahmen: {
        headline: 'Gesamtrahmen',
        gesamtwert: {
          label: 'Linienhöhe',
        },
        einzelwert: {
          label: 'max. Höhe je Bürgschaft (optional)',
        },
      },
      jahresrahmen: {
        headline: 'Jahresrahmen',
        beschreibung:
          'Bitte denken Sie daran, den Jahresrahmen bei Reduzierungen oder Erhöhungen manuell zu aktualisieren.',
        gesamtwert: {
          label: 'Linienhöhe',
        },
        einzelwert: {
          label: 'max. Höhe je Bürgschaft (optional)',
        },
        kalenderjahr: {
          label: 'Kalenderjahr',
        },
      },
      sublimits: {
        headline: 'Sublimits',
        button: {
          hinzufuegen: 'Weiteres Sublimit hinzufügen',
          entfernen: 'Löschen',
        },
        optionen: {
          keine: 'Die Avallinie hat keine Sublimits',
          hat: 'Die Avallinie hat ein oder mehrere Sublimits...',
        },
        sublimit: {
          headline: 'Sublimit',
          buergschaftsarten: 'Bürgschaftsarten',
          optionen: {
            gesamtwert: 'Sublimit in Euro',
            anteil: 'Sublimit in % der Linienhöhe',
          },
          maxGesamtwert: 'Höhe des Sublimits',
          maxAnteil: 'Höhe des Sublimits',
          maxEinzelwert: 'max. Höhe je Bürgschaft (optional)',
        },
      },
    },
    avalzins: {
      title: 'Avalzins konfigurieren',
      beschreibung:
        'Bitte geben Sie optional den mit dem Bürgen vereinbarten Zinssatz ein. Falls die Avallinie Sublimits hat, kann ein unterschiedlicher Zinssatz pro Sublimit eingetragen werden.',
      gesamt: {
        headline: 'Zinssatz Gesamtrahmen',
        input: 'Zinssatz',
      },
      sublimit: {
        headline: 'Zinssatz Sublimit',
        input: 'Zinssatz',
      },
    },
    stichtag: {
      title: 'Auslastung am Stichtag definieren',
      beschreibung:
        'Sie können optional die Auslastung an einem bestimmten Stichtag eintragen. Mithilfe des Stichtags müssen Sie Papierbürgschaften, die vor dem Stichtag ausgestellt wurden, nicht mehr manuell hinzufügen, es sei denn, sie wurden nach dem Stichtag enthaftet oder teilenthaftet.',
      weitereInformationen:
        'Weitere Informationen darüber, wie Sie die Auslastungsdaten aktuell halten, finden Sie { link }.',
      linkText: 'hier',
      button: 'Avallinie hinzufügen',
      gesamt: {
        headline: 'Auslastung Gesamtrahmen',
        input: 'Auslastung',
        date: 'Stichtag',
      },
      sublimit: {
        headline: 'Auslastung Sublimit',
        input: 'Auslastung',
        date: 'Stichtag',
      },
    },
  },
} as const;

export const avallinieHinzufuegenWorkflow: AvallinieHinzufuegenWorkflowSchema = {
  contentHeader: 'Avallinie hinzufügen',
};

export const avallinieBearbeitenWorkflow: AvallinieBearbeitenWorkflowSchema = {
  contentHeader: 'Avallinie bearbeiten',
};

export const inanspruchnahmeWorkflow: AuftraggeberInanspruchnahmeWorkflowSchema = {
  contentHeader: 'Bürgschaft in Anspruch nehmen',
  banner: {
    erfolg: {
      text: 'Die Inanspruchnahme wurde beim Bürgen übermittelt.',
    },
  },
  steps: {
    details: {
      title: 'Inanspruchnahme beschreiben',
      beschreibung:
        'Mit dieser Funktion können Sie Ihre Inanspruchnahme an den Bürgen übermitteln. Für die Inanspruchnahme gelten die mit dem Bürgen vereinbarten Voraussetzungen, für deren Einhaltung allein Sie verantwortlich sind. ',
      footer:
        'Falls nach Ihrem Wissen erforderlich, können Sie hier Unterlagen zur Konkretisierung Ihrer Inanspruchnahme zu dieser Bürgschaft hochladen. Sie können bis zu 10 Dateien (z.B. Anschreiben) mit maximal 10 MB pro Datei hochladen. Mehrere Dokumente können zu einer Datei zusammengefasst werden.',
      footerLegalAddon:
        'Der Bürge behält sich vor, bei Bedarf weitere Unterlagen und Informationen anzufordern.',
      form: {
        grundInanspruchname: {
          label: 'Begründung der Inanspruchnahme',
          placeholder:
            'Bitte legen Sie den Sachverhalt dar, warum die Bürgschaft in Anspruch genommen wird...',
        },
        hoeheInanspruchname: {
          label: 'Höhe der Inanspruchnahme',
        },
        dokumente: {
          label: 'Dokumente',
        },
      },
    },
    ansprechpartner: {
      title: 'Ansprechpartner angeben',
      beschreibung:
        'Geben Sie für mögliche Rückfragen des Bürgen die Kontaktdaten eines Ansprechpartners sowie ggf. das interne Aktenzeichen an. Falls die Anschrift des Ansprechpartners von der Adresse auf der Bürgschaft abweicht, können Sie diese hier auch eintragen.',
      rows: {
        ansprechpartner: {
          title: 'Ansprechpartner',
          meineKontaktdatenEintragen: 'Meine Kontaktdaten eintragen',
        },
        aktenzeichen: {
          title: 'Aktenzeichen',
        },
        postadresse: {
          title: 'Anschrift des Ansprechpartners',
        },
      },
    },
    pruefen: {
      title: 'Eingaben prüfen',
      beschreibung: 'Bitte überprüfen Sie die von Ihnen angegebenen Daten.',
      rows: {
        inanspruchnahme: {
          title: 'Inanspruchnahme',
          subtitles: {
            hoehe: 'Höhe der Inanspruchnahme',
            aktenzeichen: 'Aktenzeichen',
            grund: 'Grund der Inanspruchnahme',
            ansprechpartner: 'Ansprechpartner',
            anschrift: 'Anschrift',
            unterlagen: 'Unterlagen',
          },
        },
        nutzererklaerung: {
          title: 'Nutzererklärung',
          fehler: 'Bitte stimmen Sie der Nutzererklärung zu, um den Prozess fortzuführen',
        },
      },
    },
  },
};

export const teilenthaftungEintragenWorkflow: TeilenthaftungEintragenWorkflowSchema = {
  contentHeader: 'Teilenthaftung eintragen',
  banner: {
    erfolg: {
      text: 'Die Teilenthaftung wurde erfolgreich eingetragen.',
    },
    warnung: {
      header: 'Enthaftung der Bürgschaft',
      text: 'Mit einer Teilenthaftung in Höhe des gesamten, aktuellen Bürgschaftsbetrags gilt die Bürgschaft als enthaftet. Verwenden Sie bitte den Enthaftungsprozess oder ändern Sie ihre Eingabe, falls dies nicht erwünscht ist.',
      textteile: {
        reduzierte: 'reduzierte ',
        weitere: 'weitere ',
        hinweisTextHeader:
          'Die Bürgschaft wurde ursprünglich mit einem Betrag von { urspruenglicherBuergschaftsbetrag} € ausgestellt. Durch eine oder mehrere vorherige Teilenthaftungen wurde dieser Betrag bereits reduziert.',
        weitereHinweisTextHeader:
          'Bisher wurden zu dieser Bürgschaft folgende Teilenthaftungen vorgenommen:',
        hinweisTextBisherigeTeilenthaftungen:
          'Der { zusatzReduziert }Bürgschaftsbetrag wurde von { zwischenBetrag } € um { zusatzWeitere }{ hoechstBetrag } € auf einen Betrag von { reduzierterBetrag } € reduziert.',
      },
    },
  },
  eintragen: {
    title: 'Minderungs- oder reduzierten Betrag eingeben',
    beschreibung:
      'Bitte geben Sie den neuen, reduzierten Bürgschaftsbetrag oder den gewünschten Minderungsbetrag ein, um eine weitere Teilenthaftung vorzunehmen.',
    input: {
      aktuellerBuergschaftsbetrag: 'Aktueller Bürgschaftsbetrag',
      minderungsbetrag: 'Minderungsbetrag',
      hoechstbetrag: 'Reduzierter Bürgschaftsbetrag',
    },
    uebergeordneterFehler: {
      text: 'Bitte passen Sie den Minderungsbetrag oder den reduzierten Bürgschaftsbetrag an.',
      weitereText:
        'Gültige Beträge haben einen Wert von mindestens 0,01 EUR und liegen unter dem aktuellen Bürgschaftsbetrag.',
    },
    button: {
      eintragen: 'Teilenthaftung eintragen',
    },
  },
};

export const vertragDetailSite: VertragDetailSiteSchema = {
  contentHeader: 'Kreditvertrag im Detail',
  banner: {
    erfolg: {
      textZugeordnet: 'Die Bürgschaften wurden dem Kreditvertrag zugeordnet.',
      textZuordnungAufgehoben:
        'Die Vertragszuordnung wurde für die ausgewählten Bürgschaften aufgehoben.',
    },
  },
  details: {
    vertragsnummer: 'Vertragsnummer',
    kreditnehmer: 'KREDITNEHMER',
    internerVerwendungshinweis: 'Interner Verwendungshinweis',
  },
  tabellen: {
    unternehmen: {
      tab: 'Unternehmen',
      searchPlaceholder: 'Suche nach Unternehmen',
    },
    buergschaften: {
      tab: 'Bürgschaften',
      searchPlaceholder: 'Suche nach Auftraggeber oder Bürgschaftsnummer',
      inanspruchnahmeTooltip: 'Unterlagen zur Inanspruchnahme an Bürgen übermittelt.',
      teilenthaftungTooltip: 'Reduzierter Betrag nach Teilenthaftung.',
      emptyState:
        'Dem Kreditvertrag wurden noch keine Bürgschaften zugeordnet oder Sie haben für die zugeordneten Bürgschaften keine Sichtberechtigung',
      button: {
        aufheben: 'Zuordnung aufheben',
        zuordnen: 'Bürgschaften zuordnen',
      },
    },
  },
};

export const vertragDetailZuordnenWorkflow: VertragZuordnenWorkflowSchema = {
  contentHeader: 'Bürgschaften zuordnen',
  button: 'Bürgschaften zuordnen',
  auswahl: {
    title: 'Bürgschaften auswählen',
    error: {
      nichtAusgewaehlt: 'Bitte wählen Sie eine Option aus',
      keineNichtZugeordnetenBuergschaften:
        'Alle ausgestellten Bürgschaften der { buergeName } wurden bereits einem Kreditvertrag zugeordnet',
      keineBuergschaften: 'Keine ausgestellten Bürgschaften der { buergeName } gefunden',
    },
    beschreibung:
      'Bitte wählen Sie die Bürgschaften aus, die im Rahmen des Kreditvertrags ausgestellt wurden.',
    radioOptionen: {
      alle: {
        label: 'Alle ausgestellten Bürgschaften der { buergeName } zuordnen',
        subheading:
          'Alle ausgestellten Bürgschaften der { buergeName }, die noch keinem Vertrag zugeordnet sind, werden diesem Kreditvertrag zugeordnet.',
      },
      einzeln: {
        label: 'Nur ausgewählte Bürgschaften zuordnen...',
        subheading:
          'Wählen Sie aus den ausgestellten, noch nicht zugeordneten Bürgschaften der { buergeName } diejenigen aus, die dem Kreditvertrag zugeordnet werden sollen.',
      },
    },
  },
  table: {
    emptyState:
      'Es sieht so aus, als wären keine Bürgschaften auf der Plattform hinterlegt oder alle bereits Verträgen zugeordnet.',
    error: 'Bitte wählen Sie mindestens eine Bürgschaft aus',
    placeholder: 'Suche nach Bürgschaftsnummer',
  },
};

export const vertragAnlegenWorkflow: VertragAnlegenWorkflowSchema = {
  contentHeader: 'Kreditvertrag hinzufügen',
  eingeben: {
    title: 'Vertragsdaten eingeben',
    beschreibung: 'Bitte geben Sie die Vertragsdaten ein.',
    input: {
      headline: 'Vertragsdaten',
      buerge: {
        label: 'Bürge',
        beantragen: 'Die Anlage eines fehlenden Bürgen beantragen',
      },
      kreditnehmer: {
        label: 'Kreditnehmer',
      },
      vertragsnummer: {
        label: 'Vertragsnummer',
      },
    },
  },
  verknuepfen: {
    title: 'Unternehmen verknüpfen',
    beschreibung:
      'Bitte wählen Sie die Unternehmen aus, die diesen Vertrag für die Beauftragung und Ausstellung von Bürgschaften verwenden dürfen.',
    suchePlaceholder: 'Suche nach Unternehmen',
    table: {
      header: {
        unternehmen: 'Unternehmen',
        anschrift: 'Anschrift',
        status: 'Status',
      },
      mutterunternehmen: 'Mutterunternehmen',
      kreditnehmer: 'Kreditnehmer',
    },
  },
  zusammenfassung: {
    title: 'Eingaben prüfen',
    beschreibung: {
      vertragsbuerge:
        'Bitte überprüfen Sie die von Ihnen angegebenen Daten. Nachdem Sie den Vertrag hinzufügt haben, wird dieser zur Prüfung an Ihren Bürgen gesendet.',
      fremdbuerge:
        'Bitte überprüfen Sie die von Ihnen angegebenen Daten. Da es sich bei diesem Vertrag um einen nicht an Trustlog teilnehmenden Bürgen handelt, können Sie diese Daten jederzeit bearbeiten.',
    },
    button: 'Vertrag hinzufügen',
    groups: {
      headline: 'Vertragsdaten',
      buerge: 'Bürge',
      kreditnehmer: 'Kreditnehmer',
      vertragsnummer: 'Vertragsnummer',
      verknupefteUnternehmen: 'Verknüpfte Unternehmen',
    },
    internerVerwendungshinweis: {
      headline: 'Interner Verwendungshinweis',
      placeholder:
        'Beschreiben Sie Besonderheiten, die bei der Verwendung des Vertrages zu beachten sind...',
    },
    irreversibleText:
      'Bitte beachten Sie, dass das Hinzufügen eines Vertrages mit einem an Trustlog teilnehmenden Bürgen nicht rückgängig gemacht werden kann.',
    hinweisBanner: {
      vertragsbuerge: 'Der Kreditvertrag wurde hinzugefügt und liegt dem Bürgen zur Prüfung vor.',
      fremdbuerge: 'Der Kreditvertrag wurde hinzugefügt.',
    },
  },
};

export const vertragBearbeitenWorkflowSchema: VertragBearbeitenWorkflowSchema = {
  hinweisbanner: {
    nichtLoeschbarMitZugeordneterAvallinie:
      'Kreditverträge können nur gelöscht werden, wenn sie keiner Avallinie zugeordnet sind.',
    avallinieOeffnen: 'Avallinie öffnen',
  },
};

export const buergschaftBeauftragenWorkflowSchema: BuergschaftBeauftragenWorkflowSchema = {
  contentHeader: 'Bürgschaft beauftragen',
  auftragsvorlage: {
    bannerHeader:
      'Dieser Auftrag kann nicht als Vorlage verwendet werden. Die Begründung dafür ist:',
    auftraggeber: {
      nicht_aktiv_oder_inkognito:
        'Der Auftraggeber ist nicht mehr aktiv oder möchte keine digitale Bürgschaften mehr erhalten.',
    },
    auftragnehmer: {
      nicht_aktiv: 'Das als Auftragnehmer ausgewählte Unternehmen ist nicht mehr aktiv.',
      kreditvertrag: {
        nicht_verknuepft: 'Der Auftragnehmer ist mit keinem aktiven Kreditvertrag verknüpft.',
      },
    },
    text: {
      nicht_aktiv: 'Der im Auftrag verwendete Text ist nicht mehr aktiv.',
    },
    kreditvertrag: {
      nicht_gefunden:
        'Das als Auftragnehmer ausgewählte Unternehmen ist nicht mehr mit dem Kreditvertrag verknüpft.',
      nicht_aktiv: 'Der im Auftrag verwendete Kreditvertrag ist nicht mehr aktiv.',
    },
  },
  basisdaten: {
    title: 'Basisdaten eingeben',
    beschreibung:
      'Bitte geben Sie die Basisdaten der Bürgschaft ein, die zur Ermittlung der geeigneten Verträge und Texte erforderlich sind.',
    buergschaftsdaten: {
      headline: 'Bürgschaftsdaten',
      betrag: {
        label: 'Bürgschaftsbetrag',
      },
      buergschaftsart: {
        label: 'Bürgschaftsart',
      },
    },
    vertragspartner: {
      headline: 'Vertragspartner',
      auftragnehmer: {
        label: 'Auftragnehmer',
      },
      auftraggeber: {
        label: 'Auftraggeber',
        checkboxLabel: 'Auftraggeberdaten manuell eingeben (nicht empfohlen)',
        inlineMessage:
          'Wenn Sie die Auftraggeberdaten manuell eintragen, kann die Bürgschaft nicht digital\n' +
          '      ausgestellt werden. Falls der Auftraggeber an Trustlog teilnimmt, verwenden Sie bitte die\n' +
          '      Auftraggebersuche.',
      },
    },
  },
  vertragAuswahl: {
    title: 'Kreditvertrag auswählen',
    beschreibung: 'Bitte wählen Sie den Vertrag aus, der für diesen Auftrag verwendet werden soll.',
    fehlermeldung: {
      required: 'Bitte wählen Sie einen Kreditvertrag aus',
      empty: 'Der gewählte Auftragnehmer ist mit keinem Kreditvertrag verknüpft',
    },
    inlineMessage: {
      keinPremium:
        'Verwalten Sie Ihre Avallinien an einem Ort. Behalten Sie den Überblick über die Auslastung Ihrer Gesamtrahmen, inklusive Sublimits und Konditionen, sowie darüber, wann zuletzt bei welchem Bürgen eine Bürgschaft ausgestellt wurde. Verfügbar als Teil unserer Erweiterung zum ',
      aval: 'Avallinienmanagement',
    },
    emptyState: 'text hier',
    filter: {
      appropriateVertraege: 'Nur geeignete Verträge anzeigen',
    },
    vertragTabelle: {
      buerge: 'Bürge',
      kreditvertrag: 'Kreditvertrag',
      auslastung: 'Auslastung',
      zinssatz: 'Zinssatz',
      letztesAval: 'Letztes Aval',
      vertragsnummer: 'Vertragsnummer',
      kreditnehmer: 'Kreditnehmer',
      icon: {
        info: {
          header: 'Interner Verwendungshinweis',
        },
        error: {
          header: 'Vertrag nicht geeignet:',
          notYetApprovedLabel: 'Noch nicht vom Bürgen freigegeben (Status: In Prüfung)',
          guaranteeTypeNotSupportedByThisContract:
            'Bürgschaftsart ist für diesen Bürgen nicht freigeschaltet',
          rejectedLabel: 'Vom Bürgen abgelehnt (Status: Abgelehnt)',
          gesamtlimitAvallinieExceeded: 'Bürgschaftsbetrag übersteigt verfügbaren Gesamtrahmen',
          gesamtwertSublimitExceeded:
            'Bürgschaftsbetrag übersteigt verfügbaren Gesamtrahmen (Sublimit)',
          einzelwertAvallinieExceeded: 'Bürgschaftsbetrag übersteigt max. Höhe je Bürgschaft',
          einzelwertSublimitExceeded:
            'Bürgschaftsbetrag übersteigt max. Höhe je Bürgschaft (Sublimit)',
        },
      },
    },
  },
  textAuswahl: {
    title: 'Text auswählen',
    beschreibung:
      'Wählen Sie den gewünschten Bürgschaftstext aus, der für die Bürgschaft verwendet werden soll. ' +
      'Welche Optionen verfügbar sind, hängt von Ihrem Auftraggeber und Ihrem Bürgen ab.',
    buergschaftstext: {
      headline: 'Bürgschaftstext',
      checkbox: 'Einen abweichenden Text verwenden (nicht empfohlen)',
      inlineMessage:
        'Die Auswahl eines abweichenden Textes kann zur Ablehnung der Bürgschaft durch den Auftraggeber führen.',
      options: {
        auftraggeberText: {
          label: 'Vom Auftraggeber hinterlegten Text verwenden',
          text: 'Der Auftraggeber hat für diese Bürgschaftsart einen eigenen Text in Trustlog hinterlegt. Bitte verwenden Sie möglichst diesen Text für die auszustellende Bürgschaft.',
        },
        buergenText: {
          label: 'Bürgentext verwenden',
          text: 'Verwenden Sie für die auszustellende Bürgschaft den Standardtext des Bürgens.',
        },
        oeffentlichenText: {
          label: 'Öffentlichen Text verwenden',
          text: 'Verwenden Sie für die auszustellende Bürgschaft einen öffentlichen Text.',
          art: 'Art des öffentlichen Textes',
        },
        individuellenText: {
          label: 'Individuellen Text hochladen',
          text: 'Laden Sie eine abweichende Textvorlage hoch, die für die auszustellende Bürgschaft verwendet werden soll. Der Inhalt wird anschließend vom Bürgen geprüft.',
        },
      },
    },
    vertragsgrundlage: {
      headline: 'Vertragsgrundlage',
      label: 'Vertragsgrundlage',
    },
    modal: {
      headline: 'Textvorschau',
      loading: 'Das Dokument wird geladen...',
    },
  },
  buergschaftsdaten: {
    title: 'Angaben zum Bau- oder Werkvertrag',
    beschreibung: 'Bitte geben Sie die Daten zum Bau- oder Werkvertrag ein.',
    bauOderWerkvertrag: {
      headline: 'Bau- oder Werkvertrag',
      vertragsdatum: 'Vertragsdatum',
      vertragsnummer: 'Vertragsnummer',
      auftragssumme: 'Auftragssumme',
      auftragssummeProzent: 'Auftragssumme in % der Bausumme',
    },
    artDerArbeit: {
      headline: 'Art der Arbeit',
      placeholder: 'Bitte beschreiben Sie die Art der Arbeit...',
    },
  },
  versandweg: {
    title: 'Laufzeit und Ausstellungsart auswählen',
    beschreibung:
      'Bitte wählen Sie die Laufzeit und die Ausstellungsart der Bürgschaft aus. Sofern verschiedene Ausstellungswege zur Verfügung stehen, können Sie hier eine Auswahl treffen.',
    laufzeit: {
      headline: 'Laufzeit',
      inlineMessage: 'Der gewählte Text erfordert eine {befristung} Laufzeit',
      options: {
        unbefristet: 'Unbefristet',
        befristet: 'Befristet',
      },
      ablaufdatum: 'Ablaufdatum (voraussichtlich)',
      befristungsdatum: 'Befristungsdatum',
    },
    ausstellung: {
      headline: 'Art der Ausstellung',
      options: {
        digital: {
          label: 'Als digitale Bürgschaft in Trustlog',
          text: 'Die Ausstellung soll als digitale Bürgschaft in Trustlog erfolgen. Nachdem der Bürge die Bürgschaft ausgestellt hat, erhält der Auftraggeber diese sofort zur Prüfung. Auch alle weiteren Bürgschaftsvorgänge erfolgen digital.',
        },
        papier: {
          label: 'Als Papierbürgschaft per Post',
          text: 'Die Ausstellung des Bürgschaftsoriginals soll als Papierbürgschaft erfolgen und per Post an eine von Ihnen angegebene Adresse versendet werden.',
        },
        pdf: {
          label: 'Als PDF-Bürgschaft per E-Mail ',
          text: 'Die Ausstellung soll als PDF-Bürgschaft erfolgen. Sobald diese erstellt ist, erhalten Sie eine Mitteilung in Trustlog und können die Bürgschaft zum selbst Weiterleiten herunterladen.',
        },
      },
    },
    versandAdresse: {
      headline: 'Versandadresse für Papierbürgschaft',
      name: 'Name',
      adresszusatz: 'Adresszusatz (optional)',
      andereAdresse: 'Eine andere Adresse...',
      auftragnehmer: 'Auftragnehmer',
      auftraggeber: 'Auftraggeber',
      inlineMessage: {
        headline: 'Wichtiger Hinweis für Aufträge an die VHV: ',
        text: 'Der Versand der beantragten Bürgschaft erfolgt grundsätzlich immer an den Versicherungsnehmer. In einzelnen Fällen ist der Versand direkt an Ihren Auftraggeber möglich und wird von der VHV mit einer Gebühr von € 25,- berechnet. Mit dem Absenden des Antrages erklären Sie sich mit der Gebühr einverstanden. Sollten Sie den Versand der beantragten Bürgschaft an eine Niederlassung innerhalb Ihrer Firmengruppe wünschen, so entstehen hierfür keine zusätzlichen Kosten.',
      },
    },
    weitereOptionen: {
      headline: 'Weitere Optionen',
      kurier: 'Zustellung per Kurier (kostenpflichtig)',
      einschreiben: 'Zustellung per Einschreiben (kostenpflichtig)',
    },
  },
  weitereDaten: {
    title: 'Weitere Angaben erfassen',
    beschreibung:
      'Geben Sie die Kontaktdaten eines Ansprechpartners für mögliche Rückfragen des Bürgen an. Außerdem können Sie den Auftrag mit einem internen Vermerk kennzeichnen.',
    ansprechpartner: {
      headline: 'Ansprechpartner',
      button: 'Meine Kontaktdaten eintragen',
    },
    aktenvermerk: {
      headline: 'Aktenvermerk',
      internerVermerk: 'Interner Vermerk',
    },
    preAnnouncement: {
      headline: 'Bürgschaftsankündigung',
      description:
        'Optional erhält Ihr Auftraggeber eine E-Mail, sobald der Bürge die Bürgschaft ausgestellt hat. So ist der Auftraggeber frühzeitig informiert. Gleichzeitig laden wir ihn ein, sich auch auf Trustlog zu registrieren und so künftig Bürgschaften digital erhalten und sofort prüfen zu können. Sollten Sie dies nicht wünschen, lassen Sie das Eingabefeld frei.',
      notice:
        'Die Bürgschaftsankündigung enthält Bürgschaftsdaten. Stellen Sie sicher, dass die E-Mail an eine berechtigte Person versendet wird.',
      emailLabel: 'E-Mail-Adresse',
      messageLabel: 'Erfassen Sie eine Nachricht an den Auftraggeber…',
      ccLabel: 'Ich möchte die E-Mail in Kopie (CC) erhalten.',
    },
    avaleigenschaften: {
      headline: 'Weitere Avaleigenschaften',
      toggle: 'Zahlung auf erstes Anfordern',
      fehlermeldung: 'Bitte stimmen Sie der Nutzererklärung zu, um den Prozess fortzuführen',
    },
  },
  zweitpruefung: {
    entscheidung: {
      title: 'Zweitprüfer auswählen',
      beschreibung:
        'Bitte wählen Sie aus, wer die Aufgabe für die erforderliche Zweitprüfung erhalten soll.',
      cards: {
        first: {
          label: 'Alle berechtigten Nutzer können die Zweitprüfung durchführen',
          text: 'Die Aufgabe zur Prüfung dieses Auftragsentwurfs ist für alle Nutzer mit dem Zweitprüfungsrecht sichtbar.',
        },
        second: {
          label: 'Ein ausgewählter Nutzer mit der Zweitprüfung beauftragen',
          text: 'Sie können den Zweitprüfer aus einer Liste aller Nutzer mit Zweitprüfungsrecht auswählen. Die Aufgabe zur Prüfung dieses Auftragsentwurfs ist nur für den ausgewählten Nutzer sichtbar.',
        },
      },
    },
  },
  eingabenPruefen: {
    title: 'Eingaben prüfen',
    beschreibung: {
      digital: {
        text:
          'Bitte überprüfen Sie in der unten aufgeführten Bürgschaftsvorschau die von Ihnen angegebenen Daten{suffix}. \n\n' +
          'Da der Auftraggeber ebenfalls bei Trustlog registriert ist, wird die Bürgschaft nach Prüfung und Ausstellung durch den Bürgen dem Auftraggeber als {weg} zur Verfügung gestellt.',
        weg: 'digitale Bürgschaft direkt über Trustlog',
      },
      papier: {
        text:
          'Bitte überprüfen Sie in der unten aufgeführten Bürgschaftsvorschau die von Ihnen angegebenen Daten{suffix}. \n\n' +
          'Die Bürgschaft wird  nach Prüfung durch den Bürgen als {weg} ausgestellt. Das Bürgschaftsoriginal soll an untenstehende Adresse versendet werden.',
        weg: 'Papierbürgschaft per Post',
      },
      pdf: {
        text:
          'Bitte überprüfen Sie in der unten aufgeführten Bürgschaftsvorschau die von Ihnen angegebenen Daten{suffix}. \n\n' +
          'Die Bürgschaft wird Ihnen nach Prüfung und Ausstellung durch den Bürgen als {weg} bereitgestellt. Sie erhalten hierzu eine Mitteilung in Trustlog. Im Anschluss können Sie die Bürgschaft herunterladen und an Ihren Auftraggeber weiterleiten.',
        weg: 'PDF-Bürgschaft',
      },
      suffix: ', bevor Sie für diesen Auftragsentwurf eine Zweitprüfung beauftragen',
    },
    button: {
      beauftragen: 'Bürgschaft beauftragen',
      zweitpruefen: 'Zweitprüfung beauftragen',
    },
    banner: {
      erfolg: {
        header: 'Bürgschaft wurde beauftragt',
        text: 'Der Auftrag für eine Bürgschaft für {auftraggeber} wurde zur Prüfung an die {buerge} übermittelt.',
        button: {
          oeffnen: 'Auftrag öffnen',
          herunterladen: 'Auftrag herunterladen',
        },
      },
      zweitpruefung: {
        text: 'Der Auftrag wurde erfasst und die Zweitprüfung beauftragt.',
        button: {
          oeffnen: 'Auftragsentwurf öffnen',
        },
      },
    },
    uebersicht: {
      entwurf: 'ENTWURF',
      achtung: {
        header: '',
        text: '',
      },
    },
  },
};

export const auftragZweitpruefenWorkflowSchema: ZweitpruefenWorkflowSchema = {
  contentHeader: 'Auftrag prüfen',
  banner: {
    text: {
      akzeptiert: 'Der Auftrag wurde freigegeben und an den Bürgen übermittelt.',
      akzeptiertMitAenderung:
        'Der Auftrag wurde mit Korrekturwunsch freigeben. Sobald der Erstprüfer die Korrektur akzeptiert hat, wird der Auftrag am Bürgen abgesendet.',
      abgelehnt: 'Der Auftrag wurde abgelehnt.',
    },
  },
  auftragsvorlage: {
    bannerHeader: 'Auftragsentwurf kann nicht mehr beauftragt werden',
    bannerText:
      'Dieser Auftragsentwurf kann mit den aktuellen Daten nicht an den Bürgen übermittelt werden. Die Begründung dafür lautet:',
    bannerDescription:
      'Bitte stornieren Sie den Auftrag. Die berechtigten Nutzer werden über die Stornierung und deren Begründung informiert.',
    stornierenButton: 'Auftrag stornieren',
    auftraggeber: {
      nicht_aktiv_oder_inkognito:
        'Der Auftraggeber ist nicht mehr aktiv oder möchte keine digitale Bürgschaften mehr erhalten.',
    },
    auftragnehmer: {
      nicht_aktiv: 'Das als Auftragnehmer ausgewählte Unternehmen ist nicht mehr aktiv.',
      kreditvertrag: {
        nicht_verknuepft: 'Der Auftragnehmer ist mit keinem aktiven Kreditvertrag verknüpft.',
      },
    },
    text: {
      nicht_aktiv: 'Der im Auftrag verwendete Text ist nicht mehr aktiv.',
    },
    kreditvertrag: {
      nicht_gefunden:
        'Das als Auftragnehmer ausgewählte Unternehmen ist nicht mehr mit dem Kreditvertrag verknüpft.',
      nicht_aktiv: 'Der im Auftrag verwendete Kreditvertrag ist nicht mehr aktiv.',
    },
  },
  details: {
    title: 'Auftragsdaten und Bürgschaftstext prüfen',
    beschreibung:
      'Bitte prüfen Sie den unten stehenden Auftragsentwurf und geben Sie auf der nächsten Seite an, ob Sie diesen akzeptieren oder ablehnen möchten.',
  },
  entscheidung: {
    title: 'Auftragsentwurf akzeptieren?',
    beschreibung:
      'Wählen Sie aus, ob Sie den unten stehenden Auftragsentwurf akzeptieren oder ablehnen möchten.',
    cards: {
      first: {
        label: 'Auftrag freigeben',
        text: 'Sie akzeptieren den Auftragsentwurf wie unten dargestellt. Der Bürgschaftsauftrag wird im Anschluss direkt dem Bürgen übermittelt.',
        primaryLabel: 'Auftrag freigeben',
      },
      second: {
        label: 'Auftrag mit Korrekturwunsch freigeben',
        text: 'Sie akzeptieren den Auftrag wie unten dargestellt, nur mit einer oder mehreren Anpassungen. Sie können die Auftragsdaten im nächsten Schritt bearbeiten und die Korrektur im Anschluss kommentieren.',
      },
      third: {
        label: 'Auftragsentwurf ablehnen',
        text: 'Sie lehnen den Auftragsentwurf ab und können im nächsten Schritt den Ablehnungsgrund kommentieren. ',
      },
    },
  },
  aenderung: {
    title: 'Korrektur vorschlagen',
    beschreibung: 'Hier steht Platzhalter',
  },
  ablehnen: {
    title: 'Auftragsentwurf ablehnen',
    beschreibung: 'Sie lehnen den Auftragsentwurf ab. Bitte geben Sie einen Ablehnungsgrund ein.',
    platzhalter: 'Beschreiben Sie den Grund für die Ablehnung...',
    primaryLabel: 'Auftragsentwurf ablehnen',
  },
};

export const projektFormSchema: ProjektFormSchema = {
  contentHeader: {
    erstellen: 'Projekt hinzufügen',
    bearbeiten: 'Projekt bearbeiten',
  },
  banner: {
    csvHochladen: {
      textPrefix:
        'Sie möchten mehrere Projekte hochladen? Wir laden gerne eine Liste Ihrer Projekte in Trustlog hoch. Für weitere Informationen, kontaktieren Sie Ihren Kundenbetreuer oder schauen Sie in unserem ',
      linkText: 'Handbuch',
      linkUrl:
        'https://support.trustlog.de/start/projektimport.html?utm_campaign=aus-plattform&utm_content=banner',
    },
    loeschen: {
      header: 'Das Projekt enthält noch zugeordnete Bürgschaften',
      text: 'Das Projekt kann nicht gelöscht werden, da diesem noch mindestens eine Bürgschaft zugeordnet ist. Um das Projekt zu löschen, entfernen Sie bitte alle zugeordneten Bürgschaften.',
    },
  },
};

export const tabellenKonfigurationSchema: TabellenKonfigurationSchema = {
  header: 'Spalten auswählen',
  spaltenAuswaehlen: {
    titel: 'Tabellenspalten',
    beschreibung: {
      hinweis: 'Sie können hier für diese Tabelle bestimmte Spalten anzeigen oder ausblenden.',
      festeSpalten:
        'Die Spalten "Bürge" und "Status" sind immer aktiv und können nicht ausgeblendet werden.',
      mindestAnzahl: 'Es müssen insgesamt mindestens 4 Spalten aktiv sein.',
    },
    spalteNichtDeaktivierbar: 'Spalte kann nicht deaktiviert werden.',
    fehler: {
      zuWenigSpalten: 'Es müssen minimal 4 Spalten aktiviert sein.',
    },
  },
  weitereOptionen: {
    titel: 'Weitere Optionen',
    standardsortierung: 'Standardsortierung',
    sortierungInaktivWarnung:
      'Sie haben die initiale Sortierung auf eine Spalte gelegt, die in der Tabelle zurzeit nicht angezeigt wird.',
    filterInaktiveLabel:
      'Bereits enthaftete, abgelehnte sowie umgetauschte Bürgschaften ausblenden.',
  },
};

export const einstellungenSchema: EinstellungenWorkflowSchema = {
  title: 'Einstellungen',
  contentHeader: 'Einstellungen',
  buergschaftsverwaltung: {
    headline: 'Debitorische Bürgschaftsverwaltung',
  },
  mehrAugenPrinzip: {
    label: 'Zweitprüfung für Aufträge',
    description:
      'Aktivieren Sie das 4-Augen-Prinzip, um Bürgschaftsaufträge erst nach einer zweiten Freigabe verbindlich einzureichen.',
    banner: {
      activated: 'Die Zweitprüfung für Aufträge wurde aktiviert.',
      deactivated: 'Die Zweitprüfung für Aufträge wurde deaktiviert.',
    },
    activate: {
      contentHeader: 'Zweitprüfung aktivieren',
      button: 'Zweitprüfung aktivieren',
      hint: {
        title: 'Über Zweitprüfung für Aufträge',
        description:
          'Aktivieren Sie das 4-Augen-Prinzip, um Bürgschaftsaufträge erst nach einer zweiten Freigabe verbindlich einzureichen. So schützen Sie Ihr Unternehmen vor versehentlichen oder nicht autorisierten Aufträgen.',
        warningHead: 'Bitte beachten Sie folgende Hinweise:',
        warning1: 'Die Zweitprüfung gilt für alle Aufträge und alle Unternehmen Ihres Kontos.',
        warning2:
          'Jeder Auftrag muss zwingend geprüft und freigegeben werden – ein Überspringen der Prüfaufgabe ist nicht möglich.',
        warning3:
          'Vor- und Nachname aller beteiligten Prüfer werden auf Wunsch des Bürgen an diesen weitergegeben. Mit der Aktivierung stimmen Sie der Weitergabe dieser Daten zu.',
        conclusion:
          'In den nächsten Schritten bestimmen Sie zuerst eine Rolle für die Zweitprüfung, dann legen Sie fest, welche Nutzer diese Rolle übernehmen.',
      },
      roleTypeSelection: {
        title: 'Rollen für Zweitprüfung konfigurieren',
        description:
          'Für das Vier-Augen-Prinzip werden eine oder mehrere Rollen mit dem Recht zur Zweitprüfung benötigt. Bitte wählen Sie, wie Sie diese einrichten möchten.',
        hint: 'Nach der Aktivierung müssen alle neuen Bürgschaftsaufträge von einem Zweitprüfer freigegeben werden, bevor sie eingereicht werden können.',
        optionA: {
          label: 'Eine bestehende Rolle verwenden',
          text: 'Wählen Sie eine oder mehrere vorhandene Rollen aus und erweitern Sie diese um die Rechte der Zweitprüfung. Es werden nur Rollen angezeigt, denen derzeit aktive Nutzer zugewiesen sind.',
          errorMessage: 'Bitte wählen Sie mindestens eine Rolle aus.',
        },
        optionB: {
          label: 'Eine neue Rolle hinzufügen',
          text: 'Erstellen Sie im nächsten Schritt eine neue Rolle für die Zweitprüfung. Die relevanten Rechte werden automatisch zugewiesen.',
        },
      },
      newRole: {
        title: 'Darstellung',
        description: 'Bitte wählen Sie, wie die Rolle in der Plattform dargestellt werden soll.',
        display: {
          headline: 'Darstellung',
          name: 'Name',
          namePlaceholder: 'Name der Rolle',
          defaultName: 'Zweitprüfer:in',
          avatar: 'Avatar',
        },
        permissions: {
          headline: 'Berechtigungen',
          selectAll: 'Alle auswählen',
          deselectAll: 'Alle abwählen',
        },
      },
      existingRole: {
        title: 'Rolle auswählen',
        description: 'Bitte geben Sie die weiteren Bürgschaftsdaten ein.',
        errorMessage: {
          required: 'Bitte wählen Sie eine Rolle aus',
        },
        table: {
          role: 'Rolle',
          address: 'Anschrift',
          status: 'Status',
        },
      },
      userSelection: {
        title: 'Nutzer für Zweitprüfung konfigurieren',
        description:
          'Bitte bestimmen Sie, welche Nutzer die Zweitprüfung durchführen sollen. Diese erhalten die im vorherigen Schritt konfigurierte Rolle.',
        optionA: {
          label: 'Einen neuen Nutzer hinzufügen',
          text: 'Legen Sie einen neuen Nutzer an und weisen Sie ihm die vorher gewählte Rolle für die Zweitprüfung zu.',
        },
        optionB: {
          label: 'Aus bestehenden Nutzern auswählen',
          text: 'Wählen Sie einen oder mehrere vorhandene Nutzer aus und weisen Sie ihnen die vorher gewählte Rolle für die Zweitprüfung zu.',
        },
      },
      newUser: {
        title: 'Nutzer hinzufügen',
        description:
          'Bitte geben Sie Name und Kontaktdaten des neuen Nutzers an und legen Sie fest, für welche Unternehmen er eine Sichtberechtigung erhalten soll.',
      },
      existingUser: {
        title: 'Nutzer auswählen',
        description:
          'Bitte wählen Sie aus, welche Nutzer die Zweitprüfung durchführen sollen. Diese erhalten die vorher konfigurierte Rolle.',
        errorMessage: {
          required: 'Bitte wählen Sie einen Nutzer aus',
        },
        table: {
          name: 'Name',
          email: 'E-Mail-Adresse',
          role: 'Aktuelle Rolle',
        },
      },
      conclusion:
        'Mit einem Klick auf „Zweitprüfung aktivieren" wird die Zweitprüfung für alle Aufträge aktiviert.',
    },
    disable: {
      modal: {
        title: 'Zweitprüfung für Aufträge deaktivieren',
        description:
          'Sind Sie sicher, dass Sie die Zweitprüfung für Aufträge deaktivieren möchten? Bitte beachten Sie folgende Hinweise:',
        warning1:
          "Alle Aufträge, die sich noch im Status 'Zweitprüfung' befinden, werden automatisch dem Bürgen übermittelt.",
        warning2: 'Bestehende Rechte für die Zweitprüfung werden den jeweiligen Rollen entzogen.',
        warning3:
          'Bitte beachten Sie, dass diese Änderungen nicht rückgängig gemacht werden können.',
        conclusion:
          'Nach Deaktivierung der Zweitprüfung können Aufträge sofort ohne eine zweite Freigabe eingereicht werden. Ihr Unternehmen ist also nicht mehr vor versehentlichen oder nicht autorisierten Aufträgen geschützt.',
        primary: 'Zweitprüfung deaktivieren',
        secondary: 'Abbrechen',
      },
    },
  },
};
